import React, { useState, Children } from 'react';
import { ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { COASegment, AccountType } from './ChartOfAccounts';
import { AccountTypeBadge } from './AccountTypeBadge';
interface COATreeViewProps {
  segments: COASegment[];
  selectedSegmentId?: string;
  onSelectSegment: (segment: COASegment) => void;
  accountTypes: AccountType[];
  locale: string;
}
export function COATreeView({
  segments,
  selectedSegmentId,
  onSelectSegment,
  accountTypes,
  locale
}: COATreeViewProps) {
  // Track expanded nodes
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  // Toggle node expansion
  const toggleNode = (segmentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedNodes({
      ...expandedNodes,
      [segmentId]: !expandedNodes[segmentId]
    });
  };
  // Recursive function to render the tree
  const renderTree = (segments: COASegment[], level: number = 0) => {
    return segments.map(segment => {
      const hasChildren = segment.children && segment.children.length > 0;
      const isExpanded = expandedNodes[segment.id] === true;
      const isSelected = segment.id === selectedSegmentId;
      const accountType = accountTypes.find(at => at.id === segment.accountTypeId);
      return <div key={segment.id} className="select-none" data-id="element-8999">
          <div className={`flex items-center py-2 px-2 hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-indigo-50' : ''}`} style={{
          paddingLeft: `${level * 16 + 8}px`
        }} onClick={() => onSelectSegment(segment)} data-id="element-9000">
            {hasChildren ? <button onClick={e => toggleNode(segment.id, e)} className="mr-1 focus:outline-none" data-id="element-9001">
                {isExpanded ? <ChevronDownIcon className="h-4 w-4 text-gray-500" data-id="element-9002" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500" data-id="element-9003" />}
              </button> : <span className="w-4 mr-1" data-id="element-9004"></span>}
            <div className="flex items-center flex-1" data-id="element-9005">
              <span className={`font-medium ${!segment.isActive ? 'text-gray-400' : ''}`} data-id="element-9006">
                {segment.segmentCode}
              </span>
              <span className={`ml-2 ${!segment.isActive ? 'text-gray-400' : ''}`} data-id="element-9007">
                {segment.segmentName}
              </span>
              {/* Status dot */}
              <span className={`ml-2 h-2 w-2 rounded-full ${segment.isActive ? 'bg-green-500' : 'bg-gray-300'}`} title={segment.isActive ? 'Active' : 'Inactive'} data-id="element-9008"></span>
              {/* Account type badge */}
              {accountType && <div className="ml-2" data-id="element-9009">
                  <AccountTypeBadge type={accountType} size="sm" data-id="element-9010" />
                </div>}
            </div>
          </div>
          {/* Render children if expanded */}
          {hasChildren && isExpanded && <div data-id="element-9011">{renderTree(segment.children || [], level + 1)}</div>}
        </div>;
    });
  };
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9012">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9013">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9014">
          GL Structure
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-9015">
          Select an account segment to view or edit details
        </p>
      </div>
      <div className="overflow-y-auto" style={{
      maxHeight: 'calc(100vh - 350px)'
    }} data-id="element-9016">
        {segments.length === 0 ? <div className="p-4 text-center text-gray-500" data-id="element-9017">
            No account segments found. Click "Add Root Segment" to create one.
          </div> : renderTree(segments)}
      </div>
    </div>;
}